import type { ReviewStats } from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'

// Threes and below are the ones that hurt the listing, so they get the
// red; four is a warning; the two no-review buckets are neutral greys so
// the eye lands on the stars first.
export const STAR_STYLE: Record<string, { cls: string; label: string }> = {
  '5': { cls: 'bg-mint', label: '5 stars' },
  '4': { cls: 'bg-amber', label: '4 stars' },
  '3': { cls: 'bg-danger', label: '3 stars' },
  '2': { cls: 'bg-[#c62828]', label: '2 stars' },
  '1': { cls: 'bg-[#8e0e1e]', label: '1 star' },
  no_review: { cls: 'bg-white/25', label: 'no review, window closed' },
  pending: { cls: 'bg-white/10', label: 'no review yet, window open' },
}

export function StarBar({
  stars,
  noReview = 0,
  pending = 0,
  className = 'h-4',
  ariaLabel,
}: {
  stars: ReviewStats['portfolio']['stars']
  noReview?: number
  pending?: number
  className?: string
  ariaLabel: string
}) {
  const segs: Array<[string, number]> = [
    ['5', stars[5]],
    ['4', stars[4]],
    ['3', stars[3]],
    ['2', stars[2]],
    ['1', stars[1]],
    ['no_review', noReview],
    ['pending', pending],
  ]
  const total = segs.reduce((a, [, n]) => a + n, 0)
  return (
    <div
      className={`flex w-full gap-0.5 overflow-hidden rounded-full bg-white/5 ${className}`}
      role="img"
      aria-label={ariaLabel}
    >
      {segs.map(([k, n]) =>
        n > 0 ? (
          <Tooltip key={k}>
            <TooltipTrigger asChild>
              <div
                className={`${STAR_STYLE[k].cls} h-full cursor-default`}
                style={{ width: `${(100 * n) / Math.max(1, total)}%` }}
              />
            </TooltipTrigger>
            <TooltipContent>
              {STAR_STYLE[k].label}: {n} ({Math.round((100 * n) / Math.max(1, total))}%)
            </TooltipContent>
          </Tooltip>
        ) : null
      )}
    </div>
  )
}

export function StarLegend() {
  return (
    <div className="flex flex-wrap gap-4 text-[11px] text-textMuted">
      {Object.entries(STAR_STYLE).map(([k, s]) => (
        <span key={k}>
          <i className={`mr-1 inline-block h-2 w-2 rounded-sm ${s.cls}`} />
          {s.label}
        </span>
      ))}
    </div>
  )
}

/** Portfolio distribution: every closed stay, from five stars down to never reviewed. */
export function StarDistribution({ stats }: { stats: ReviewStats }) {
  const p = stats.portfolio
  const rows: Array<[string, number]> = [
    ['5', p.stars[5]],
    ['4', p.stars[4]],
    ['3', p.stars[3]],
    ['2', p.stars[2]],
    ['1', p.stars[1]],
    ['no_review', p.no_review],
    ['pending', p.pending_no_review],
  ]
  // One denominator for the bar and the legend: the sum of what is drawn.
  // (Reviews are counted over every review record, closed stays over
  // reservations, so they do not reconcile to a single "stays" figure.)
  const total = rows.reduce((a, [, n]) => a + n, 0)
  return (
    <Card>
      <CardHeader>
        <CardTitle>Every stay, by outcome</CardTitle>
        <CardDescription>
          {p.reviews} reviews by star, {p.no_review} closed stays that never reviewed, and{' '}
          {p.pending_no_review} still inside their review window without one.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <StarBar
          stars={p.stars}
          noReview={p.no_review}
          pending={p.pending_no_review}
          className="h-6"
          ariaLabel={`Portfolio: ${rows.map(([k, n]) => `${STAR_STYLE[k].label} ${n}`).join(', ')}`}
        />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs sm:grid-cols-4 lg:grid-cols-7">
          {rows.map(([k, n]) => (
            <div key={k} className="flex items-center gap-2">
              <i className={`inline-block h-2.5 w-2.5 rounded-sm ${STAR_STYLE[k].cls}`} />
              <span className="text-textMuted">{STAR_STYLE[k].label}</span>
              <span className="ml-auto tabular-nums text-text">
                {n}
                <span className="text-textMuted">
                  {' '}
                  · {Math.round((100 * n) / Math.max(1, total))}%
                </span>
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
